import React, { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import './modal.scss';
import { ModalTitle } from './modal-title';
import { ModalStack } from './modal-stack';

export interface ModalProps {
	className?: string;
	dialogueClassName?: string;
	dark?: boolean;
	Header?: React.ReactNode;
	CloseButton?: React.ReactNode;
	visible: boolean;
	wide?: boolean;
	fullHeight?: boolean;
	Footer?: React.ReactNode;
	fixed?: boolean;
	onClickOutside?: () => void;
	dialogueRef?: React.MutableRefObject<HTMLDivElement | null>;
	noAnimation?: boolean;
	onAnimationStart?: (animation: 'show' | 'hide') => void;
	onAnimationEnd?: (animation: 'show' | 'hide') => void;
}

export function Modal(props: PropsWithChildren<ModalProps>) {
	const id = useRef<number>();
	useEffect(() => {
		id.current = ModalStack.getId();
		return () => {
			if (id.current) {
				ModalStack.isHidden(id.current);
			}
		};
	}, []);

	const [initialState, setInitialState] = useState(props.visible === false);
	useEffect(() => {
		if (props.visible) {
			setInitialState(false);
			if (id.current) {
				ModalStack.isVisible(id.current);
			}
		} else {
			if (id.current) {
				ModalStack.isHidden(id.current);
			}
		}
	}, [props.visible]);

	const backdropRef = useRef<HTMLDivElement>(null);
	const backdropClicked = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			if (e.target === backdropRef.current) {
				props.onClickOutside?.();
			}
		},
		[props.onClickOutside]
	);

	const onAnimationStart = useCallback((e: React.AnimationEvent) => {
		if (e.animationName === 'inline-modal-show') {
			props.onAnimationStart?.('show');
		} else if (e.animationName === 'inline-modal-hide') {
			props.onAnimationStart?.('hide');
		}
	}, []);
	const onAnimationEnd = useCallback((e: React.AnimationEvent) => {
		if (e.animationName === 'inline-modal-show') {
			props.onAnimationEnd?.('show');
		} else if (e.animationName === 'inline-modal-hide') {
			props.onAnimationEnd?.('hide');
		}
	}, []);

	return (
		<div
			ref={backdropRef}
			onClick={backdropClicked}
			className={`inline-modal${props.visible ? ' show' : initialState ? '' : ' hide'}${props.dark ? ' dark' : ''}${
				props.fixed ? ' inline-modal-fixed' : ''
			}${props.noAnimation ? ' no-animation' : ''}${props.className ? ' ' + props.className : ''}`}
			onAnimationStart={onAnimationStart}
			onAnimationEnd={onAnimationEnd}
		>
			<div
				ref={props.dialogueRef}
				className={`inline-modal-dialogue${props.wide ? ' inline-modal-dialogue-lg' : ''}${props.fullHeight ? ' full' : ''}${props.dialogueClassName ? ' ' + props.dialogueClassName : ''}`}
			>
				{(props.Header || props.CloseButton) && (
					<div className='inline-modal-header'>
						{props.Header ? (
							typeof props.Header === 'string' ? (
								<ModalTitle>{props.Header}</ModalTitle>
							) : (
								<div className='inline-modal-header-container'>{props.Header}</div>
							)
						) : (
							<span></span>
						)}
						{props.CloseButton}
					</div>
				)}
				<div className='inline-modal-body'>{props.children}</div>
				{props.Footer && <div className='inline-modal-footer'>{props.Footer}</div>}
			</div>
		</div>
	);
}
